/* ==========================================================================
   Paw Power — PDP modules
   Vanilla ES5-safe. No dependencies. Loaded deferred from layout/theme.liquid.

   Integrates with Prestige 4.1 by dispatching the theme's own events:
     - 'product:added'        -> makes the mini-cart / drawer re-render
     - 'theme:loading:start'  -> theme loading bar
     - 'theme:loading:end'

   Ordergroove: option B. When the shopper picks a subscription, the
   mix-and-match bundle builder hides and the standard single-jar form is used.
   ========================================================================== */
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     Money formatting (mirrors Shopify's money filter)
     ---------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    var fmt = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');

    function pad(num, size) {
      var s = String(num);
      while (s.length < size) s = '0' + s;
      return s;
    }

    function thousands(num, sep, decSep, decimals) {
      if (isNaN(num) || num === null) return 0;
      var d = decimals == null ? 2 : decimals;
      var fixed = (num / 100.0).toFixed(d);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (sep || ','));
      var centsPart = parts[1] ? (decSep || '.') + parts[1] : '';
      return dollars + centsPart;
    }

    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(placeholder);
    var token = match ? match[1] : 'amount';

    switch (token) {
      case 'amount':
        value = thousands(cents, ',', '.', 2);
        break;
      case 'amount_no_decimals':
        value = thousands(cents, ',', '.', 0);
        break;
      case 'amount_with_comma_separator':
        value = thousands(cents, '.', ',', 2);
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = thousands(cents, '.', ',', 0);
        break;
      case 'amount_with_apostrophe_separator':
        value = thousands(cents, "'", '.', 2);
        break;
      default:
        value = thousands(cents, ',', '.', 2);
    }
    return fmt.replace(placeholder, value);
  }

  /* ----------------------------------------------------------------------
     Small helpers
     ---------------------------------------------------------------------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function parseJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent || el.innerHTML); } catch (e) { return null; }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* ======================================================================
     Ordergroove bridge — option B
     Detects whether the shopper has chosen a subscription, and broadcasts
     'pp:purchase-option' so the bundle builder can hide itself.

     Detection is deliberately defensive: Ordergroove has shipped several
     widget versions. If none of the signals are present we report
     'onetime', which keeps the bundle visible (safe default).
     ====================================================================== */
  var PurchaseOption = (function () {
    var current = 'onetime';

    function readForm(form) {
      if (!form) return 'onetime';

      // 1. The line-item properties Ordergroove injects into the product form.
      //    This is the signal that actually determines whether the cart line
      //    becomes a subscription, so it is the most reliable one.
      var freq = form.querySelector('[name="properties[shipping_interval_frequency]"]');
      var unit = form.querySelector('[name="properties[shipping_interval_unit_type]"]');
      if (freq && freq.value && freq.value !== '0' && freq.value !== 'null') return 'subscription';
      if (unit && unit.value && unit.value !== '0' && unit.value !== 'null') return 'subscription';

      // 2. Ordergroove's own radio group, when it renders in light DOM.
      var ogRadio = form.querySelector('input[type="radio"][name*="frequency"]:checked, input[type="radio"].og-radio:checked');
      if (ogRadio) {
        var v = (ogRadio.value || '').toLowerCase();
        if (v && v !== 'onetime' && v !== 'one-time' && v !== '0' && v !== 'false') return 'subscription';
      }

      // 3. The <og-offer> custom element exposing its state as an attribute.
      var offer = form.querySelector('og-offer, .og-offer, [data-og-offer]');
      if (offer) {
        var attr = offer.getAttribute('selected') || offer.getAttribute('data-selected') || '';
        if (attr.toLowerCase() === 'subscription' || attr === 'true') return 'subscription';
      }

      return 'onetime';
    }

    function evaluate() {
      var form = $('form[action^="/cart/add"]');
      var next = readForm(form);
      if (next === current) return;
      current = next;
      document.dispatchEvent(new CustomEvent('pp:purchase-option', {
        detail: { mode: current }
      }));
    }

    function init() {
      var form = $('form[action^="/cart/add"]');
      if (!form) return;

      var run = debounce(evaluate, 60);

      // Ordergroove mutates the form rather than firing a stable public event,
      // so observe it directly and also catch ordinary change events.
      form.addEventListener('change', run);
      form.addEventListener('click', run);

      if (window.MutationObserver) {
        new MutationObserver(run).observe(form, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['value', 'selected', 'data-selected', 'checked', 'class']
        });
      }

      // Ordergroove's documented ready hooks, if present.
      document.addEventListener('og:ready', run);
      document.addEventListener('og:offer:rendered', run);
      document.addEventListener('optIn', run);
      document.addEventListener('optOut', run);

      evaluate();
    }

    return {
      init: init,
      get: function () { return current; },
      evaluate: evaluate
    };
  })();

  /* ======================================================================
     Bundle builder — option C (mix & match flavours)
     ====================================================================== */
  function initBundle(root) {
    if (root.__ppInit) return;
    root.__ppInit = true;

    var config = parseJSON($('[data-pp-bundle-config]', root));
    if (!config) return;

    var tiers = (config.tiers || []).slice().sort(function (a, b) { return a.qty - b.qty; });
    var maxQty = config.maxQty || 12;
    var servingsPerJar = config.servingsPerJar || 0;

    var items = $$('[data-pp-bundle-item]', root);
    var fillEl = $('[data-pp-fill]', root);
    var nudgeEl = $('[data-pp-nudge]', root);
    var priceEl = $('[data-pp-total-price]', root);
    var wasEl = $('[data-pp-total-was]', root);
    var saveEl = $('[data-pp-save]', root);
    var perServeEl = $('[data-pp-per-serving]', root);
    var addBtn = $('[data-pp-add]', root);
    var errorEl = $('[data-pp-error]', root);
    var ticks = $$('[data-pp-tick]', root);
    var shipBar = $('[data-pp-shipbar]', root);
    var shipFill = $('[data-pp-shipbar-fill]', root);
    var shipText = $('[data-pp-shipbar-text]', root);
    var shipThreshold = shipBar ? parseInt(shipBar.getAttribute('data-pp-shipbar-threshold'), 10) || 0 : 0;
    // the empty-cart copy is authored in Liquid as the element's initial text
    // — captured once here rather than duplicated as a data attribute
    var shipEmptyText = shipText ? shipText.textContent : '';

    // ---- state ----------------------------------------------------------
    var state = {};
    items.forEach(function (item) {
      var id = item.getAttribute('data-variant-id');
      state[id] = parseInt(item.getAttribute('data-initial-qty'), 10) || 0;
    });

    function totalQty() {
      var n = 0;
      for (var k in state) { if (state.hasOwnProperty(k)) n += state[k]; }
      return n;
    }

    function grossCents() {
      var sum = 0;
      items.forEach(function (item) {
        var id = item.getAttribute('data-variant-id');
        var price = parseInt(item.getAttribute('data-price'), 10) || 0;
        sum += price * (state[id] || 0);
      });
      return sum;
    }

    function tierFor(qty) {
      var found = null;
      for (var i = 0; i < tiers.length; i++) {
        if (qty >= tiers[i].qty) found = tiers[i];
      }
      return found;
    }

    function nextTier(qty) {
      for (var i = 0; i < tiers.length; i++) {
        if (qty < tiers[i].qty) return tiers[i];
      }
      return null;
    }

    // ---- render ---------------------------------------------------------
    function render() {
      var qty = totalQty();
      var gross = grossCents();
      var tier = tierFor(qty);
      var pct = tier ? tier.percent : 0;
      var discount = Math.round(gross * pct / 100);
      var net = gross - discount;

      // steppers
      items.forEach(function (item) {
        var id = item.getAttribute('data-variant-id');
        var soldOut = item.getAttribute('data-available') === 'false';
        var val = state[id] || 0;
        var valEl = $('[data-pp-val]', item);
        var minus = $('[data-pp-minus]', item);
        var plus = $('[data-pp-plus]', item);

        if (valEl) valEl.textContent = val;
        if (minus) minus.disabled = val <= 0;
        if (plus) plus.disabled = soldOut || qty >= maxQty;
        item.classList.toggle('is-active', val > 0);
      });

      // progress bar — measured against the largest configured tier
      var target = tiers.length ? tiers[tiers.length - 1].qty : 1;
      var ratio = Math.min(qty / target, 1);
      if (fillEl) fillEl.style.width = (ratio * 100) + '%';

      ticks.forEach(function (t) {
        var need = parseInt(t.getAttribute('data-pp-tick'), 10) || 0;
        t.classList.toggle('is-reached', qty >= need);
      });

      // nudge
      if (nudgeEl) {
        var nt = nextTier(qty);
        if (qty === 0) {
          nudgeEl.innerHTML = config.emptyText || 'Add jars to build your bundle.';
        } else if (nt) {
          var need2 = nt.qty - qty;
          nudgeEl.innerHTML = 'Add <strong>' + need2 + ' more jar' + (need2 === 1 ? '' : 's') +
            '</strong> to save <strong>' + nt.percent + '%</strong>';
        } else {
          nudgeEl.innerHTML = 'Nice — you have unlocked <strong>' + pct + '% off</strong>, the best price.';
        }
      }

      // totals
      if (priceEl) priceEl.textContent = formatMoney(net);
      if (wasEl) {
        wasEl.textContent = discount > 0 ? formatMoney(gross) : '';
        wasEl.style.display = discount > 0 ? '' : 'none';
      }
      // Short enough to sit in the summary pill; the percentage is already
      // carried by the progress ticks and the nudge line.
      if (saveEl) {
        saveEl.textContent = discount > 0 ? 'You save ' + formatMoney(discount) : '';
      }

      // free shipping progress — mirrors the reference's "Add $X more for
      // free shipping" line, computed live from what the customer would
      // actually be charged (net of the bundle discount)
      if (shipBar && shipThreshold > 0) {
        var shipRatio = Math.min(net / shipThreshold, 1);
        if (shipFill) shipFill.style.width = (shipRatio * 100) + '%';
        if (shipText) {
          if (qty === 0) {
            shipText.textContent = shipEmptyText;
            shipBar.classList.remove('is-unlocked');
          } else if (net >= shipThreshold) {
            shipText.textContent = 'You’ve unlocked free shipping';
            shipBar.classList.add('is-unlocked');
          } else {
            shipText.textContent = 'Add ' + formatMoney(shipThreshold - net) + ' more for free shipping';
            shipBar.classList.remove('is-unlocked');
          }
        }
      }

      // cost per serving — the angle the current page is missing entirely
      if (perServeEl) {
        if (servingsPerJar > 0 && qty > 0) {
          var perServing = net / (qty * servingsPerJar);
          perServeEl.textContent = 'About ' + formatMoney(Math.round(perServing)) + ' per serving';
          perServeEl.style.display = '';
        } else {
          perServeEl.style.display = 'none';
        }
      }

      if (addBtn) {
        addBtn.disabled = qty === 0;
        var label = $('[data-pp-add-label]', addBtn);
        if (label) {
          label.textContent = qty === 0
            ? (config.emptyButton || 'Select your flavours')
            : (config.addButton || 'Add bundle to cart') + ' — ' + formatMoney(net);
        }
      }
    }

    // ---- interaction ----------------------------------------------------
    root.addEventListener('click', function (e) {
      var plus = e.target.closest ? e.target.closest('[data-pp-plus]') : null;
      var minus = e.target.closest ? e.target.closest('[data-pp-minus]') : null;
      if (!plus && !minus) return;

      var item = (plus || minus).closest('[data-pp-bundle-item]');
      if (!item) return;
      var id = item.getAttribute('data-variant-id');

      if (plus) {
        if (totalQty() >= maxQty) return;
        if (item.getAttribute('data-available') === 'false') return;
        state[id] = (state[id] || 0) + 1;
      } else {
        state[id] = Math.max(0, (state[id] || 0) - 1);
      }
      if (errorEl) errorEl.textContent = '';
      render();
    });

    // ---- add to cart ----------------------------------------------------
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var payload = [];
        items.forEach(function (item) {
          var id = item.getAttribute('data-variant-id');
          var q = state[id] || 0;
          if (q > 0) payload.push({ id: parseInt(id, 10), quantity: q });
        });
        if (!payload.length) return;

        addBtn.classList.add('is-loading');
        if (errorEl) errorEl.textContent = '';
        document.dispatchEvent(new CustomEvent('theme:loading:start'));

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: payload })
        }).then(function (response) {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
          addBtn.classList.remove('is-loading');

          if (!response.ok) {
            return response.json().then(function (content) {
              if (errorEl) {
                errorEl.textContent = (content && content.description) ||
                  'Sorry, we could not add that to your cart.';
              }
            });
          }

          // Let Prestige's mini-cart re-render itself.
          root.dispatchEvent(new CustomEvent('product:added', {
            bubbles: true,
            detail: { quantity: totalQty() }
          }));

          // Reset the builder so it is not re-submitted by accident.
          for (var k in state) { if (state.hasOwnProperty(k)) state[k] = 0; }
          render();
        }).catch(function () {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
          addBtn.classList.remove('is-loading');
          if (errorEl) errorEl.textContent = 'Network error. Please try again.';
        });
      });
    }

    // ---- option B: one purchase path visible at a time --------------------
    // Subscription  -> bundle hidden, theme's own add-to-cart restored so the
    //                  Ordergroove line-item properties submit with the form.
    // One-time      -> bundle is the purchase path, theme's add-to-cart and
    //                  dynamic checkout button hidden to avoid two buttons.
    //
    // The theme button is only ever hidden from JS, never from CSS, so if this
    // script fails to run the page still has a working way to buy.
    var themeAtc = $('.ProductForm__AddToCart');
    var themePay = $('.shopify-payment-button');

    function applyMode(mode) {
      var isSub = mode === 'subscription';

      root.hidden = isSub;
      root.setAttribute('aria-hidden', isSub ? 'true' : 'false');

      if (themeAtc) themeAtc.style.display = isSub ? '' : 'none';
      if (themePay) themePay.style.display = isSub ? '' : 'none';
    }

    document.addEventListener('pp:purchase-option', function (e) {
      applyMode(e.detail.mode);
    });
    applyMode(PurchaseOption.get());

    render();
  }

  /* ======================================================================
     Accordions (ingredients + FAQ) — animated, accessible
     ====================================================================== */
  function initAccordion(root) {
    if (root.__ppInit) return;
    root.__ppInit = true;

    var single = root.getAttribute('data-pp-accordion') === 'single';
    var buttons = $$('[data-pp-acc-btn]', root);

    function close(btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      panel.style.height = panel.scrollHeight + 'px';
      // force reflow so the transition runs from a real height
      panel.offsetHeight; // eslint-disable-line no-unused-expressions
      panel.style.height = '0px';
      btn.setAttribute('aria-expanded', 'false');
    }

    function open(btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      panel.style.height = panel.scrollHeight + 'px';
      btn.setAttribute('aria-expanded', 'true');

      // Once open, release the fixed height so the panel can reflow with its
      // content (responsive rewrap, lazy images). transitionend is not
      // guaranteed to fire — a cancelled or zero-duration transition skips it —
      // so a timer backs it up. Whichever lands first wins.
      var released = false;
      var release = function () {
        if (released) return;
        released = true;
        clearTimeout(timer);
        panel.removeEventListener('transitionend', onEnd);
        // Only release if this panel is still open.
        if (btn.getAttribute('aria-expanded') === 'true') panel.style.height = 'auto';
      };
      var onEnd = function (e) {
        if (e.propertyName && e.propertyName !== 'height') return;
        release();
      };
      var timer = setTimeout(release, 400);
      panel.addEventListener('transitionend', onEnd);
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        if (single && !expanded) {
          buttons.forEach(function (other) {
            if (other !== btn && other.getAttribute('aria-expanded') === 'true') close(other);
          });
        }
        if (expanded) close(btn); else open(btn);
      });
    });
  }

  /* ======================================================================
     Sticky add to cart
     ====================================================================== */
  function initSticky(root) {
    if (root.__ppInit) return;
    root.__ppInit = true;

    var btn = $('[data-pp-sticky-add]', root);
    var form = $('form[action^="/cart/add"]');
    var watchSel = root.getAttribute('data-pp-watch') || '.ProductForm__AddToCart';
    var watch = $(watchSel);

    if (!watch) return;

    if (btn) {
      btn.addEventListener('click', function () {
        // If the bundle builder is visible and has a selection, use it.
        var bundleAdd = $('.PP-Bundle:not([hidden]) [data-pp-add]');
        if (bundleAdd && !bundleAdd.disabled) {
          bundleAdd.click();
          return;
        }
        // Otherwise fall through to the theme's own add-to-cart button so
        // Ordergroove's subscription properties are submitted with the form.
        var themeBtn = form && form.querySelector('[data-action="add-to-cart"]');
        if (themeBtn) {
          themeBtn.click();
        } else {
          watch.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }

    // Reveal once the real add-to-cart button has scrolled out of view.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          root.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      }, { rootMargin: '0px 0px -80% 0px', threshold: 0 }).observe(watch);
    }

    // Keep the sticky price in step with the variant the theme has selected.
    var priceEl = $('[data-pp-sticky-price]', root);
    if (priceEl) {
      document.addEventListener('variant:changed', function (e) {
        var v = e.detail && e.detail.variant;
        if (v && typeof v.price === 'number') priceEl.textContent = formatMoney(v.price);
      });
    }
  }

  /* ======================================================================
     App block hider

     Some blocks on this product page are injected at runtime by installed
     apps, not by the theme — the "Buy in bulk and get a discount!" volume
     widget and the "Buy these products together" bundle widget. They are not
     in the theme source, so there is nothing to delete; and they do not
     render in preview sessions, so a stable CSS selector could not be
     captured. This matches them by their heading text instead and hides the
     block they belong to.

     Phrases come from a setting so the client can add or remove them without
     touching code. Matching is deliberately narrow: it only inspects heading
     elements, and it walks up at most a few levels to find the block wrapper.
     Anything hidden is logged so it can be checked.
     ====================================================================== */
  function initBlockHider() {
    var host = $('[data-pp-hide-blocks]');
    if (!host) return;

    var phrases = (host.getAttribute('data-pp-hide-blocks') || '')
      .split('|')
      .map(function (s) { return s.trim().toLowerCase(); })
      .filter(Boolean);
    if (!phrases.length) return;

    function sweep() {
      var headings = $$('h1, h2, h3, h4, .h1, .h2, .h3');
      headings.forEach(function (h) {
        var text = (h.textContent || '').trim().toLowerCase();
        if (!text) return;

        var hit = phrases.some(function (p) { return text.indexOf(p) === 0; });
        if (!hit) return;

        // Walk up to the block wrapper, but never past the page itself.
        var el = h;
        for (var i = 0; i < 4; i++) {
          if (!el.parentElement || el.parentElement === document.body) break;
          el = el.parentElement;
          if (el.tagName === 'SECTION' || el.id || (el.className || '').length > 12) break;
        }
        if (el === document.body || el.tagName === 'MAIN') return;

        el.style.display = 'none';
        el.setAttribute('data-pp-hidden-block', 'true');
        if (window.console && console.info) {
          console.info('[PP] hid app block matching "' + text.slice(0, 48) + '"');
        }
      });
    }

    sweep();
    // Apps inject late and sometimes re-render, so keep watching for a while.
    if (window.MutationObserver) {
      var obs = new MutationObserver(debounce(sweep, 200));
      obs.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { obs.disconnect(); }, 15000);
    }
  }

  /* ======================================================================
     Boot
     ====================================================================== */
  function boot() {
    initBlockHider();
    PurchaseOption.init();
    $$('[data-pp-bundle]').forEach(initBundle);
    $$('[data-pp-accordion]').forEach(initAccordion);
    $$('[data-pp-sticky]').forEach(initSticky);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-boot inside the theme editor when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target;
    $$('[data-pp-bundle]', el).forEach(initBundle);
    $$('[data-pp-accordion]', el).forEach(initAccordion);
    $$('[data-pp-sticky]', el).forEach(initSticky);
  });

  window.PPPdp = { formatMoney: formatMoney, purchaseOption: PurchaseOption };
})();

/* ==========================================================================
   PAW POWER — JUMP TO REVIEWS

   Clicking the star rating in the buy box should take you to the reviews,
   the way it does on every large store. Two things were missing: the stars
   themselves were not clickable (only the small text link was), and a plain
   #anchor jump lands the target underneath the sticky header.

   Kept as its own IIFE so it is independent of the rest of this file — if
   anything above throws, the jump link still works.
   ========================================================================== */
(function () {
  'use strict';

  var TARGET = '#judgeme_product_reviews';

  function reviewsEl() {
    return document.querySelector(TARGET) ||
           document.querySelector('.jdgm-review-widget');
  }

  /* The sticky header overlaps whatever an anchor scrolls to, so offset by
     its real height rather than a guessed constant. */
  function headerOffset() {
    var header = document.querySelector('.PP-Nav, .Header');
    var h = header ? header.getBoundingClientRect().height : 0;
    return h + 16;
  }

  function jump(e) {
    var target = reviewsEl();
    if (!target) return;                    // widget disabled - leave the link alone
    if (e) e.preventDefault();

    var top = window.pageYOffset + target.getBoundingClientRect().top - headerOffset();
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: top, behavior: reduced ? 'auto' : 'smooth' });

    /* Move focus for keyboard and screen-reader users, without letting the
       browser re-jump and undo the offset. */
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  function init() {
    var rating = document.querySelector('.PP-Buy__Rating');
    if (rating && reviewsEl()) {
      rating.style.cursor = 'pointer';
      rating.setAttribute('role', 'link');
      rating.setAttribute('tabindex', '0');
      rating.setAttribute('aria-label', 'Read customer reviews');
      rating.addEventListener('click', jump);
      rating.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') jump(e);
      });
    }

    /* Any other link pointing at the reviews gets the same offset handling. */
    Array.prototype.forEach.call(
      document.querySelectorAll('a[href="' + TARGET + '"]'),
      function (a) { a.addEventListener('click', jump); }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
